import { EFinancingCompany, EFinancingProgram } from '@if/core/types'
import {
  Alert,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material'
import { useSearchMerchants } from '@src/data/api/merchant/merchant-api'
import { debounce } from 'lodash-es'
import { ChangeEvent, memo, MouseEvent, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Merchant } from '../data/types'
import ActionsDialog from './ActionsDialog'
import InputTextField from './InputTextField'

type OnSelectSingleMerchant = (merchant: Merchant) => void
type OnSelectMultipleMerchants = (merchants: Merchant[]) => void

interface Props {
  open: boolean
  title: string
  label: string
  allowMultiple: boolean
  financingProgramId: EFinancingProgram | null
  financingCompanyId: EFinancingCompany | null
  onConfirm: OnSelectSingleMerchant | OnSelectMultipleMerchants
  onCancel: () => void
  disableConfirm?: boolean
}

const SelectMerchantDialog = ({
  onConfirm,
  onCancel,
  open,
  title,
  label,
  financingProgramId,
  financingCompanyId,
  allowMultiple,
  disableConfirm,
}: Props) => {
  const MERCHANTS_LIMIT = 25
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedMerchants, setSelectedMerchants] = useState<Merchant[]>([])

  const searchDto = useMemo(
    () =>
      debouncedQuery?.trim()
        ? {
            financingProgramId,
            financingCompanyId,
            nameContains: debouncedQuery,
            limit: MERCHANTS_LIMIT,
            isActive: true,
          }
        : null,
    [debouncedQuery, financingProgramId, financingCompanyId],
  )

  const [merchants] = useSearchMerchants(searchDto)

  const debouncedSetQuery = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedQuery(value)
        if (!value?.trim() && !allowMultiple) {
          setSelectedMerchants([])
        }
      }, 1000),
    [allowMultiple],
  )

  const updateQuery = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    debouncedSetQuery(e.target.value)
  }

  const handleItemSelected = useCallback(
    (evt: MouseEvent<HTMLElement>) => {
      const itemValue = (evt as MouseEvent<HTMLButtonElement>).currentTarget.id
      const value = merchants.find((x) => x.id === itemValue)
      if (value) {
        setSelectedMerchants([value])
      }
    },
    [merchants],
  )

  const handleItemChecked = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const itemValue = evt.target.value
      const selectedMerchant = merchants.find((x) => x.id === itemValue)
      if (selectedMerchant) {
        setSelectedMerchants((prev) => {
          const existingIndex = prev.findIndex((x) => x.id === selectedMerchant.id)
          if (existingIndex !== -1) {
            return prev.filter((x) => x.id !== selectedMerchant.id)
          }
          return [...prev, selectedMerchant]
        })
      }
    },
    [merchants],
  )

  const cancel = useCallback(() => {
    setQuery('')
    setDebouncedQuery('')
    onCancel()
  }, [onCancel])

  const confirmSelection = useCallback(() => {
    if (selectedMerchants.length > 0) {
      if (allowMultiple) {
        const callback = onConfirm as OnSelectMultipleMerchants
        callback(selectedMerchants)
      } else {
        const callback = onConfirm as OnSelectSingleMerchant
        callback(selectedMerchants[0])
      }
      setQuery('')
      setDebouncedQuery('')
    }
  }, [selectedMerchants, allowMultiple, onConfirm])

  return (
    <ActionsDialog title={title} onCancel={cancel} onConfirm={confirmSelection} open={open} disabled={disableConfirm}>
      <Grid container spacing={2} sx={{ mt: 5, width: 600 }}>
        <Grid
          size={{
            xs: 12,
            md: 14,
          }}
        >
          <InputTextField id="searchMerchantInput" onChange={updateQuery} label={label} value={query} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 14,
          }}
        >
          {debouncedQuery?.trim() && merchants.length === 0 && (
            <Alert severity="info">{t('common.searchYieldsNoResults')}</Alert>
          )}
          {debouncedQuery && merchants.length >= MERCHANTS_LIMIT && (
            <Alert severity="info">
              <Trans i18nKey="common.tooManyResultsMessage" values={{ limit: MERCHANTS_LIMIT }} />
            </Alert>
          )}
          {!debouncedQuery?.trim() && <p>{t('common.typeToStartSearch')}</p>}
          <List
            sx={{
              width: '100%',
              maxWidth: 800,
              bgcolor: 'background.paper',
              position: 'relative',
              overflow: 'auto',
              height: 300,
              '& ul': { padding: 0 },
            }}
          >
            {!allowMultiple &&
              merchants.map((merchant) => {
                return (
                  <ListItemButton
                    id={merchant.id}
                    key={merchant.id}
                    selected={selectedMerchants.find((x) => x.id === merchant.id) !== undefined}
                    onClick={handleItemSelected}
                  >
                    <ListItemText>{merchant.name}</ListItemText>
                  </ListItemButton>
                )
              })}
            {allowMultiple &&
              merchants.map((merchant) => (
                <ListItem key={merchant.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedMerchants.find((x) => x.id === merchant.id) !== undefined}
                        onChange={handleItemChecked}
                        value={merchant.id}
                      />
                    }
                    label={merchant.name}
                  />
                </ListItem>
              ))}
          </List>
        </Grid>
        {allowMultiple && (
          <Grid container spacing={1} sx={{ mt: 1, width: 600 }}>
            {selectedMerchants.map((merchant) => (
              <Grid key={merchant.id}>
                <Chip label={merchant.name} />
              </Grid>
            ))}
          </Grid>
        )}
      </Grid>
    </ActionsDialog>
  )
}
export default memo(SelectMerchantDialog)
